import React, { useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';

const fmtQty = (v) => {
  const n = Number(v) || 0;
  return n % 1 === 0 ? n.toLocaleString() : n.toFixed(2);
};

/**
 * Production / de-production adjustment with a BOM preview.
 * delta > 0 = PRODUCE units (deducts materials) · delta < 0 = de-produce
 * (returns materials). The preview shows the material impact BEFORE anything
 * is applied; the callable applies product bucket + ingredients + movement
 * logs in one transaction.
 */
const StockAdjustModal = ({ product, locations, ingredients = [], defaultLocationId, onClose, onDone }) => {
  const user = getAuth().currentUser;
  const byLocation = product.stockScope === 'byLocation';
  const [locationId, setLocationId] = useState(defaultLocationId || locations[0]?.id || '');
  const [deltaStr, setDeltaStr] = useState('');
  const [reason, setReason] = useState('production'); // production | deproduction
  const [busy, setBusy] = useState(false);

  const currentStock = byLocation
    ? Number(product.stockByLocation?.[locationId] ?? 0)
    : Number(product.stockQty ?? 0);

  const delta = (parseInt(deltaStr, 10) || 0) * (reason === 'production' ? 1 : -1);
  const recipe = Array.isArray(product.recipe) ? product.recipe : [];

  // Material impact preview (computed client-side from the recipe)
  const impacts = useMemo(() => {
    if (recipe.length === 0 || delta === 0) return [];
    return recipe.map((r) => {
      const ing = ingredients.find((x) => x.id === r.ingredientId);
      return {
        ingredientId: r.ingredientId,
        name: ing?.name || r.ingredientId,
        useUnit: ing?.useUnit || '',
        qty: (parseFloat(r.qty) || 0) * Math.abs(delta),
      };
    });
  }, [recipe, delta]);

  const apply = async () => {
    if (delta === 0) { Swal.fire('Missing', 'Enter a quantity', 'warning'); return; }
    if (byLocation && !locationId) { Swal.fire('Missing', 'Pick a location', 'warning'); return; }
    if (!byLocation && recipe.length > 0 && !locationId) { Swal.fire('Missing', 'Materials live at a location — pick where production happens', 'warning'); return; }
    setBusy(true);
    try {
      await httpsCallable(getFunctions(), 'adjustProductStock')({
        categoryId: product.categoryId,
        productId: product.id,
        delta,
        reason,
        locationId: locationId || null,
      });
      await onDone();
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ borderRadius: 16 }}>
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">Stock adjustment — {product.name}</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-6 form-group">
                <label className="font-weight-bold">Direction</label>
                <select className="form-control" value={reason} onChange={(e) => setReason(e.target.value)}>
                  <option value="production">Produce (add units)</option>
                  <option value="deproduction">De-produce / waste (remove units)</option>
                </select>
              </div>
              <div className="col-6 form-group">
                <label className="font-weight-bold">Qty (units)</label>
                <input type="number" min="0" className="form-control" value={deltaStr} onChange={(e) => setDeltaStr(e.target.value)} autoFocus />
              </div>
            </div>
            {(byLocation || recipe.length > 0) && (
              <div className="form-group">
                <label className="font-weight-bold">Location{!byLocation && recipe.length > 0 ? ' (where production happens)' : ''}</label>
                <select className="form-control" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.companyName || l.locationName}</option>)}
                </select>
              </div>
            )}
            <div className="alert alert-light border small mb-0">
              Current stock{byLocation ? ' at this location' : ''}: <strong>{fmtQty(currentStock)}</strong> units
              {delta !== 0 && <> → after: <strong>{fmtQty(Math.max(0, currentStock + delta))}</strong></>}
            </div>

            {/* BOM preview — what happens to materials */}
            {impacts.length > 0 && (
              <div className="mt-3">
                <label className="font-weight-bold small">
                  <i className="fas fa-utensils mr-1" style={{ color: '#ff6b35' }}></i>
                  Material impact preview {reason === 'production' ? '(deducted)' : '(returned to stock)'}:
                </label>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
                  {impacts.map((i, idx) => (
                    <div key={idx} className="d-flex justify-content-between small py-1" style={{ borderBottom: idx < impacts.length - 1 ? '1px dashed #f0f0f0' : 'none' }}>
                      <span>{i.name || i.ingredientId}</span>
                      <strong style={{ color: reason === 'production' ? '#dc2626' : '#16a34a' }}>
                        {reason === 'production' ? '−' : '+'}{fmtQty(i.qty)} {i.useUnit || ''}
                      </strong>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {recipe.length === 0 && delta !== 0 && (
              <small className="text-muted d-block mt-2">No recipe on this product — only the unit stock moves.</small>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn btn-primary" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={apply} disabled={busy || delta === 0}>
              {busy ? 'Applying…' : 'Apply adjustment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockAdjustModal;
